// Chart performance optimization utilities

export class ChartPerformanceOptimizer {
  constructor() {
    this.performanceData = new Map()
    this.renderQueue = []
    this.isProcessingQueue = false
    this.maxConcurrentCharts = this.getMaxConcurrentCharts()
    this.activeCharts = new Set()
  }

  // Determine optimal concurrent chart limit based on device capabilities
  getMaxConcurrentCharts() {
    const isMobile = window.innerWidth < 768
    const isLowEndDevice = this.isLowEndDevice()

    if (isMobile && isLowEndDevice) {
      return 1 // Render one chart at a time on low-end mobile
    } else if (isMobile) {
      return 2 // Limit to 2 charts on mobile
    } else if (isLowEndDevice) {
      return 2 // Limit to 2 charts on low-end desktop
    } else {
      return 4 // Allow up to 4 concurrent charts on capable devices
    }
  }

  // Detect low-end devices based on available metrics
  isLowEndDevice() {
    // Check for device memory API
    if ('deviceMemory' in navigator) {
      return navigator.deviceMemory <= 2 // 2GB or less
    }

    // Check for hardware concurrency (CPU cores)
    if ('hardwareConcurrency' in navigator) {
      return navigator.hardwareConcurrency <= 2 // 2 cores or less
    }

    // Fallback: assume low-end if mobile
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // Queue chart for optimized rendering
  queueChart(chartController, priority = 'normal') {
    const chartId = this.generateChartId(chartController)

    const queueItem = {
      id: chartId,
      controller: chartController,
      priority: priority,
      queuedAt: Date.now(),
      retryCount: 0
    }

    // Insert based on priority
    if (priority === 'high') {
      this.renderQueue.unshift(queueItem)
    } else {
      this.renderQueue.push(queueItem)
    }

    this.processQueue()
  }

  // Process the render queue
  async processQueue() {
    if (this.isProcessingQueue || this.renderQueue.length === 0) {
      return
    }

    this.isProcessingQueue = true

    while (this.renderQueue.length > 0 && this.activeCharts.size < this.maxConcurrentCharts) {
      const queueItem = this.renderQueue.shift()

      if (queueItem) {
        this.renderChart(queueItem)
      }
    }

    this.isProcessingQueue = false
  }

  // Render individual chart with performance monitoring
  async renderChart(queueItem) {
    const { id, controller, priority } = queueItem
    const startTime = performance.now()

    try {
      this.activeCharts.add(id)

      // Apply performance optimizations based on device capabilities
      this.applyChartOptimizations(controller)

      // Render the chart
      await this.performChartRender(controller)

      const renderTime = performance.now() - startTime
      this.recordPerformance(id, renderTime, true)

      // Log slow renders
      if (renderTime > 1000) { // 1 second threshold
        console.warn(`Slow chart render: ${id} took ${renderTime.toFixed(2)}ms`)
      }

    } catch (error) {
      const renderTime = performance.now() - startTime
      this.recordPerformance(id, renderTime, false, error.message)

      console.error(`Chart render failed: ${id}`, error)

      // Retry logic for failed renders
      if (queueItem.retryCount < 2) {
        queueItem.retryCount++
        setTimeout(() => {
          this.renderQueue.unshift(queueItem)
          this.processQueue()
        }, 1000 * queueItem.retryCount) // Exponential backoff
      }

    } finally {
      this.activeCharts.delete(id)

      // Process next items in queue
      setTimeout(() => this.processQueue(), 100)
    }
  }

  // Apply device-specific optimizations to chart configuration
  applyChartOptimizations(controller) {
    if (!controller.chart || !controller.chart.options) return

    const isMobile = window.innerWidth < 768
    const isLowEndDevice = this.isLowEndDevice()

    // Reduce animation duration on low-end devices
    if (isLowEndDevice) {
      if (controller.chart.options.animation) {
        controller.chart.options.animation.duration = 200
      }
      if (controller.chart.options.animations) {
        Object.keys(controller.chart.options.animations).forEach(key => {
          controller.chart.options.animations[key].duration = 200
        })
      }
    }

    // Disable hover animations on mobile
    if (isMobile) {
      if (controller.chart.options.hover) {
        controller.chart.options.hover.animationDuration = 0
      }
    }

    // Reduce point radius for better performance with large datasets
    if (controller.chart.data && controller.chart.data.datasets) {
      controller.chart.data.datasets.forEach(dataset => {
        if (dataset.data && dataset.data.length > 50) {
          dataset.pointRadius = dataset.pointRadius ? Math.min(dataset.pointRadius, 2) : 2
          dataset.pointHoverRadius = dataset.pointHoverRadius ? Math.min(dataset.pointHoverRadius, 4) : 4
        }
      })
    }

    // Optimize responsive options
    if (controller.chart.options.responsive) {
      controller.chart.options.maintainAspectRatio = !isMobile
    }
  }

  // Perform the actual chart rendering
  async performChartRender(controller) {
    return new Promise((resolve, reject) => {
      try {
        // Use requestAnimationFrame for smooth rendering
        requestAnimationFrame(() => {
          try {
            if (controller.initializeChart) {
              controller.initializeChart()
            } else if (controller.chart && controller.chart.update) {
              controller.chart.update('none') // Update without animation for performance
            }
            resolve()
          } catch (error) {
            reject(error)
          }
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  // Generate unique chart ID
  generateChartId(controller) {
    return `chart_${controller.element.id || Math.random().toString(36).substr(2, 9)}`
  }

  // Record performance metrics
  recordPerformance(chartId, renderTime, success, error = null) {
    const performanceEntry = {
      chartId,
      renderTime,
      success,
      error,
      timestamp: Date.now(),
      deviceInfo: {
        isMobile: window.innerWidth < 768,
        isLowEnd: this.isLowEndDevice(),
        memory: navigator.deviceMemory || 'unknown',
        cores: navigator.hardwareConcurrency || 'unknown'
      }
    }

    this.performanceData.set(chartId, performanceEntry)

    // Send to analytics if configured
    this.sendPerformanceMetrics(performanceEntry)
  }

  // Send performance metrics to analytics
  sendPerformanceMetrics(entry) {
    // Only send metrics in production or if explicitly enabled
    if (process.env.NODE_ENV !== 'production' && !window.ENABLE_CHART_ANALYTICS) {
      return
    }

    try {
      // Send to custom analytics endpoint
      if (window.ANALYTICS_ENDPOINT) {
        fetch(window.ANALYTICS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'chart_performance',
            data: entry
          })
        }).catch(error => {
          console.warn('Failed to send chart performance metrics:', error)
        })
      }

      // Send to Google Analytics if available
      if (window.gtag) {
        window.gtag('event', 'chart_render', {
          event_category: 'performance',
          event_label: entry.chartId,
          value: Math.round(entry.renderTime),
          custom_map: {
            success: entry.success,
            device_type: entry.deviceInfo.isMobile ? 'mobile' : 'desktop'
          }
        })
      }
    } catch (error) {
      console.warn('Failed to send performance metrics:', error)
    }
  }

  // Get performance summary
  getPerformanceSummary() {
    const entries = Array.from(this.performanceData.values())

    if (entries.length === 0) {
      return {
        totalCharts: 0,
        averageRenderTime: 0,
        successRate: 1,
        slowCharts: 0
      }
    }

    const totalRenderTime = entries.reduce((sum, entry) => sum + entry.renderTime, 0)
    const successfulRenders = entries.filter(entry => entry.success).length
    const slowCharts = entries.filter(entry => entry.renderTime > 1000).length

    return {
      totalCharts: entries.length,
      averageRenderTime: totalRenderTime / entries.length,
      successRate: successfulRenders / entries.length,
      slowCharts: slowCharts,
      slowChartRate: slowCharts / entries.length
    }
  }

  // Clear old performance data to prevent memory leaks
  clearOldPerformanceData(maxAge = 300000) { // 5 minutes default
    const cutoffTime = Date.now() - maxAge

    for (const [chartId, entry] of this.performanceData.entries()) {
      if (entry.timestamp < cutoffTime) {
        this.performanceData.delete(chartId)
      }
    }
  }

  // Optimize chart data for rendering
  optimizeChartData(data, maxDataPoints = 100) {
    if (!data || !data.datasets) return data

    const optimizedData = { ...data }

    optimizedData.datasets = data.datasets.map(dataset => {
      if (!dataset.data || dataset.data.length <= maxDataPoints) {
        return dataset
      }

      // Sample data points for large datasets
      const step = Math.ceil(dataset.data.length / maxDataPoints)
      const sampledData = []
      const sampledLabels = []

      for (let i = 0; i < dataset.data.length; i += step) {
        sampledData.push(dataset.data[i])
        if (data.labels && data.labels[i]) {
          sampledLabels.push(data.labels[i])
        }
      }

      // Update labels if they exist
      if (data.labels && sampledLabels.length > 0) {
        optimizedData.labels = sampledLabels
      }

      return {
        ...dataset,
        data: sampledData
      }
    })

    return optimizedData
  }

  // Debounce chart updates for better performance
  debounceChartUpdate(controller, delay = 300) {
    if (controller.updateTimeout) {
      clearTimeout(controller.updateTimeout)
    }

    controller.updateTimeout = setTimeout(() => {
      if (controller.chart && controller.chart.update) {
        controller.chart.update('none')
      }
    }, delay)
  }
}

// Create global instance
export const chartPerformanceOptimizer = new ChartPerformanceOptimizer()

// Clean up old performance data periodically
setInterval(() => {
  chartPerformanceOptimizer.clearOldPerformanceData()
}, 60000) // Every minute
